import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Tabs,
  Tab,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { typographyScale } from "../../../../../../../styles/theme";
import { useMrtSurfaceProps } from "../../../../../../../styles/mrtThemeProps";

import { useStructureModeling } from "../../structureModelingContext";
import { columns } from "./columnsCheckBoxes";
import {
  getComponentTab,
  getSizesOptions,
  updateDerivedFields,
  getFamilyTableOptions,
} from "./utils";

import { VideoModalBtn } from "../../../../../../../components/Modals/VideoModal";
import { tabsVideos } from "../links";
import { useToast } from "../../../../../../../context/toastContext";

const tabLabels = [
  "Angles",
  "Beams",
  "Channels",
  "Tubes",
  "Flatbars",
  "Gratings",
  "Plates",
  "Fasteners",
];

const FastenersTab = ["Bolts", "Nuts", "Washers"];

const ALL_TABS = [
  "Angles",
  "Beams",
  "Channels",
  "Tubes",
  "Flatbars",
  "Gratings",
  "Plates",
  "Bolts",
  "Nuts",
  "Washers",
];

export function PartLibTable() {
  const { toast } = useToast();
  const mrtSurfaceProps = useMrtSurfaceProps();

  const [tabIndex, setTabIndex] = useState(0);
  const [subTabIndex, setSubTabIndex] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // "update" | "updateAssembly"

  // ✅ Own the pagination state — MRT will never auto-reset it
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const currentTab = tabLabels[tabIndex];
  const isFastenersTab = currentTab === "Fasteners";
  const activeTabName = isFastenersTab ? FastenersTab[subTabIndex] : currentTab;

  const {
    getSelectedSpecTable,
    selectedSpecs,
    allTables,
    partLibTable,
    setPartLibTable,
    allCombinationTable,
    setAllCombinationTable,
  } = useStructureModeling();

  // -------------------------------
  // RAW SPEC DATA
  // -------------------------------
  const allSpecData = useMemo(() => {
    if (!Array.isArray(selectedSpecs) || selectedSpecs.length === 0) return [];
    if (typeof getSelectedSpecTable !== "function") return [];
    return selectedSpecs.flatMap(getSelectedSpecTable);
  }, [selectedSpecs, getSelectedSpecTable]);

  const specs = useMemo(() => {
    return [...new Set(allSpecData.map((item) => item?.Spec).filter(Boolean))];
  }, [allSpecData]);

  // -------------------------------
  // 1) Annotate each row with computed TabNameOnce
  // -------------------------------
  const allSpecDataWithTab = useMemo(() => {
    if (!allSpecData.length) return [];
    return allSpecData.map((row) => {
      const tab = getComponentTab(row?.Components, row?.Spec, allTables);
      return { ...row, __tabName: tab };
    });
  }, [allSpecData, allTables]);

  // -------------------------------
  // 2) Build index: spec -> tab -> component -> baseRows[]
  // -------------------------------
  const specTabCompIndex = useMemo(() => {
    const index = new Map();
    for (const row of allSpecDataWithTab) {
      const spec = row?.Spec;
      const comp = row?.Components;
      const tab = row?.__tabName;
      if (!spec || !comp || !tab) continue;

      if (!index.has(spec)) index.set(spec, new Map());
      const tabMap = index.get(spec);
      if (!tabMap.has(tab)) tabMap.set(tab, new Map());
      const compMap = tabMap.get(tab);
      if (!compMap.has(comp)) compMap.set(comp, []);
      compMap.get(comp).push(row);
    }
    return index;
  }, [allSpecDataWithTab]);

  // -------------------------------
  // 3) Cache sizes options per (spec|comp) once
  // -------------------------------
  const sizeOptionsCache = useMemo(() => {
    const cache = new Map();
    if (!specs.length) return cache;

    for (const spec of specs) {
      const rowsForSpec = allSpecDataWithTab.filter((r) => r?.Spec === spec);
      const comps = [
        ...new Set(rowsForSpec.map((r) => r?.Components).filter(Boolean)),
      ];

      for (const comp of comps) {
        const key = `${spec}|${comp}`;
        try {
          const sizes =
            getSizesOptions(allSpecDataWithTab, spec, comp, allTables) || [];
          cache.set(key, sizes);
        } catch (e) {
          console.error("getSizesOptions failed:", e);
          cache.set(key, []);
        }
      }
    }

    return cache;
  }, [specs, allSpecDataWithTab, allTables]);

  // -------------------------------
  // Rebuild allCombinationTable ONLY when specs list changes
  // -------------------------------
  const prevSpecsRef = useRef(null);

  useEffect(() => {
    const specsKey = [...specs].sort().join(",");
    if (prevSpecsRef.current === specsKey) return;
    prevSpecsRef.current = specsKey;

    if (!allSpecDataWithTab.length || !specs.length) {
      // Don't wipe a populated table while allTables/specTable are still loading
      if (allCombinationTable.length > 0) return;
      setAllCombinationTable([]);
      return;
    }

    const allRows = [];

    for (const tabName of ALL_TABS) {
      for (const spec of specs) {
        const tabMap = specTabCompIndex.get(spec);
        const compMap = tabMap?.get(tabName);
        if (!compMap) continue;

        for (const [comp, baseRows] of compMap.entries()) {
          const sizes = sizeOptionsCache.get(`${spec}|${comp}`) || [];
          if (!baseRows?.length || !sizes.length) continue;

          for (const baseRow of baseRows) {
            const orgSizeRange = baseRow?.Size;
            const filteredSizes = sizes.filter(
              (s) => s?.range === orgSizeRange,
            );

            const seen = new Set();
            const deduped = [];
            for (const s of filteredSizes) {
              const val = s?.value;
              if (!val) continue;
              if (seen.has(val)) continue;
              seen.add(val);
              deduped.push(s);
            }

            for (const sizeObj of deduped) {
              const categoryType = "Bulk";

              const newRow = {
                ...baseRow,
                select: false,
                TabName: tabName,
                Size: sizeObj?.value,
                OriginalSizeRange: orgSizeRange,
                CategoryType: categoryType,
              };

              try {
                updateDerivedFields(
                  allSpecDataWithTab,
                  (_rowIndex, key, value) => {
                    newRow[key] = value;
                  },
                  allTables,
                  0,
                  newRow,
                );
              } catch {}

              // Mirror FamilyTableCell's auto-selection so rows from tabs the
              // user hasn't visited still get a Family Table when exactly one
              // option exists.
              try {
                const familyOptions = getFamilyTableOptions(
                  allTables,
                  newRow.Components,
                  newRow.Spec,
                );

                const uniqueNames = Array.from(
                  new Set(
                    (familyOptions || [])
                      .map((cur) => String(cur?.Family_Name ?? "").trim())
                      .filter((n) => n && n !== "-"),
                  ),
                );

                if (uniqueNames.length === 1 && !newRow["Family Table"]) {
                  const onlyName = uniqueNames[0];
                  newRow["Family Table"] = onlyName;
                  const tableTarget =
                    familyOptions.find((cur) => cur.Family_Name === onlyName)
                      ?.Family_Table || "";
                  if (tableTarget) newRow.FamilyTableTarget = tableTarget;
                }
              } catch {}

              delete newRow.__tabName;
              allRows.push(newRow);
            }
          }
        }
      }
    }

    setAllCombinationTable((prevAllCombo) => {
      return allRows.map((newRow) => {
        const prevMatch = (prevAllCombo || []).find(
          (p) =>
            p?.Spec === newRow?.Spec &&
            p?.Components === newRow?.Components &&
            p?.Size === newRow?.Size &&
            p?.Material === newRow?.Material &&
            p?.TabName === newRow?.TabName,
        );
        if (prevMatch?.select) {
          return {
            ...newRow,
            ...prevMatch,
            select: true,
            "Family Table":
              prevMatch["Family Table"] ?? newRow["Family Table"],
            FamilyTableTarget:
              prevMatch.FamilyTableTarget ?? newRow.FamilyTableTarget,
          };
        }

        const savedTab = partLibTable?.[newRow?.TabName];
        if (savedTab?.length) {
          const savedRow = savedTab.find(
            (r) =>
              r?.Spec === newRow?.Spec &&
              r?.Components === newRow?.Components &&
              r?.Size === newRow?.Size &&
              r?.Material === newRow?.Material,
          );
          if (savedRow) {
            return {
              ...newRow,
              ...savedRow,
              select: true,
              TabName: newRow?.TabName,
              "Family Table":
                savedRow["Family Table"] || newRow["Family Table"],
              FamilyTableTarget:
                savedRow.FamilyTableTarget || newRow.FamilyTableTarget,
            };
          }
        }

        return newRow;
      });
    });
  }, [
    specs,
    partLibTable,
    setAllCombinationTable,
    allSpecDataWithTab,
    allTables,
    specTabCompIndex,
    sizeOptionsCache,
  ]);

  // -------------------------------
  // Current tab data
  // -------------------------------
  const currentTabData = useMemo(() => {
    return (allCombinationTable || []).filter(
      (r) => r?.TabName === activeTabName,
    );
  }, [allCombinationTable, activeTabName]);

  const selectedCountCurrent = useMemo(
    () => currentTabData.filter((r) => r?.select).length,
    [currentTabData],
  );

  const selectedCountAll = useMemo(
    () => (allCombinationTable || []).filter((r) => r?.select).length,
    [allCombinationTable],
  );

  // -------------------------------
  // Sync selected rows -> partLibTable[tab]
  // -------------------------------
  useEffect(() => {
    const selectedByTab = {};
    (allCombinationTable || []).forEach((row) => {
      if (!row?.select || !row?.TabName) return;
      if (!selectedByTab[row.TabName]) selectedByTab[row.TabName] = [];
      const { select, TabName, ...rest } = row;
      selectedByTab[row.TabName].push(rest);
    });

    setPartLibTable((prev) => {
      const updated = { ...(prev || {}) };
      let hasChanges = false;

      Object.keys(selectedByTab).forEach((tab) => {
        const prevTab = updated[tab] || [];
        const nextTab = selectedByTab[tab];

        const prevKeys = new Set(
          prevTab.map(
            (r) => `${r.Spec}|${r.Components}|${r.Size}|${r.Material}`,
          ),
        );
        const nextKeys = new Set(
          nextTab.map(
            (r) => `${r.Spec}|${r.Components}|${r.Size}|${r.Material}`,
          ),
        );

        const same =
          prevKeys.size === nextKeys.size &&
          [...prevKeys].every((k) => nextKeys.has(k));

        if (!same) {
          updated[tab] = nextTab;
          hasChanges = true;
        }
      });

      Object.keys(updated).forEach((tab) => {
        if (!selectedByTab[tab] && (updated[tab]?.length || 0) > 0) {
          updated[tab] = [];
          hasChanges = true;
        }
      });

      if (!hasChanges) return prev;
      return updated;
    });
  }, [allCombinationTable, setPartLibTable]);

  // -------------------------------
  // Handlers used by columns
  // -------------------------------
  const rowMatches = (a, b) =>
    a?.Spec === b?.Spec &&
    a?.Components === b?.Components &&
    a?.Size === b?.Size &&
    a?.Material === b?.Material &&
    a?.TabName === b?.TabName;

  const handleToggleRow = useCallback(
    (rowOriginal, checked) => {
      const tabName = rowOriginal?.TabName || activeTabName;

      setAllCombinationTable((prev) =>
        (prev || []).map((cur) =>
          rowMatches(cur, rowOriginal) ? { ...cur, select: checked } : cur,
        ),
      );

      setPartLibTable((prev) => {
        const updatedTab = ((prev || {})[tabName] || []).filter(
          (cur) =>
            !(
              cur?.Spec === rowOriginal?.Spec &&
              cur?.Components === rowOriginal?.Components &&
              cur?.Size === rowOriginal?.Size &&
              cur?.Material === rowOriginal?.Material
            ),
        );
        if (checked) {
          const { select, TabName, ...toAdd } = rowOriginal;
          updatedTab.push(toAdd);
        }
        return { ...(prev || {}), [tabName]: updatedTab };
      });
    },
    [activeTabName, setAllCombinationTable, setPartLibTable],
  );

  const handleFieldUpdate = useCallback(
    (row, key, value) => {
      const original = row.original;
      const tabName = original?.TabName || activeTabName;

      setAllCombinationTable((prev) =>
        (prev || []).map((cur) =>
          rowMatches(cur, original) ? { ...cur, [key]: value } : cur,
        ),
      );

      if (original?.select) {
        setPartLibTable((prev) => {
          const updatedTab = ((prev || {})[tabName] || []).filter(
            (cur) =>
              !(
                cur?.Spec === original?.Spec &&
                cur?.Components === original?.Components &&
                cur?.Size === original?.Size &&
                cur?.Material === original?.Material
              ),
          );
          updatedTab.push({ ...original, [key]: value });
          return { ...(prev || {}), [tabName]: updatedTab };
        });
      }
    },
    [activeTabName, setAllCombinationTable, setPartLibTable],
  );

  const handleDeleteRow = useCallback(
    (rowIndex) => {
      const rowToDelete = currentTabData[rowIndex];
      if (!rowToDelete) return;
      const tabName = rowToDelete?.TabName || activeTabName;

      setAllCombinationTable((prev) =>
        (prev || []).filter((cur) => !rowMatches(cur, rowToDelete)),
      );

      setPartLibTable((prev) => {
        const updatedTab = ((prev || {})[tabName] || []).filter(
          (cur) =>
            !(
              cur?.Spec === rowToDelete?.Spec &&
              cur?.Components === rowToDelete?.Components &&
              cur?.Size === rowToDelete?.Size &&
              cur?.Material === rowToDelete?.Material
            ),
        );
        return { ...(prev || {}), [tabName]: updatedTab };
      });
    },
    [
      currentTabData,
      activeTabName,
      setAllCombinationTable,
      setPartLibTable,
    ],
  );

  // ✅ Memoize the columns array
  const memoizedColumns = useMemo(
    () =>
      columns(allTables, handleFieldUpdate, handleDeleteRow, handleToggleRow),
    [allTables, handleFieldUpdate, handleDeleteRow, handleToggleRow],
  );

  // -------------------------------
  // Update actions
  // -------------------------------
  const handleUpdate = () => {
    setPendingAction("update");
    setOpenDialog(true);
  };

  const handleUpdateAssembly = () => {
    setPendingAction("updateAssembly");
    setOpenDialog(true);
  };

  const executeAction = async (scope) => {
    setOpenDialog(false);

    const selected =
      scope === "current"
        ? currentTabData.filter((r) => r?.select)
        : (allCombinationTable || []).filter((r) => r?.select);

    if (!selected.length) {
      toast("No items selected", { type: "warning" });
      return;
    }

    setSnackbarMessage(
      `${pendingAction === "update" ? "Update" : "Update Assembly"} in progress...`,
    );
    setSnackbarSeverity("info");
    setOpenSnackbar(true);

    try {
      if (pendingAction === "update") {
        window.electron.freecad.modifyProfiles(selected);
      } else {
        window.electron.structure.updateAssembly();
      }

      setSnackbarMessage(
        `${pendingAction === "update" ? "Updated" : "Assembly Updated"} (${
          scope === "current" ? `Current Tab: ${activeTabName}` : "All Tabs"
        } - ${selected.length} items)`,
      );
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (e) {
      console.error(e);
      setSnackbarMessage("Operation failed");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      <Tabs
        value={tabIndex}
        onChange={(e, newIndex) => {
          setTabIndex(newIndex);
          setSubTabIndex(0);
          // ✅ Reset to page 1 only when user switches tabs
          setPagination((p) => ({ ...p, pageIndex: 0 }));
        }}
      >
        {tabLabels.map((label) => (
          <Tab label={label} key={label} />
        ))}
      </Tabs>

      {isFastenersTab && (
        <Tabs
          value={subTabIndex}
          onChange={(e, newIndex) => {
            setSubTabIndex(newIndex);
            // ✅ Reset to page 1 only when user switches sub-tabs
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          }}
          sx={{ borderBottom: 1, borderColor: "divider", ml: 2 }}
        >
          {FastenersTab.map((label) => (
            <Tab label={label} key={label} />
          ))}
        </Tabs>
      )}

      <Box sx={{ p: 2, display: "flex", gap: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Button variant="contained" onClick={handleUpdate}>
          Update
        </Button>
        <Button variant="contained" onClick={handleUpdateAssembly}>
          Update Assembly
        </Button>

        <VideoModalBtn
          src={tabsVideos.partsLibrary}
          sx={{ marginLeft: "auto" }}
        />

        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Select Update Scope</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to update{" "}
            {pendingAction === "update" ? "Family" : "Assembly"} for:
          </DialogContentText>

          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <DialogContentText sx={{ fontWeight: "bold" }}>
              • Current Tab Only: {activeTabName} ({selectedCountCurrent}{" "}
              selected)
            </DialogContentText>
            <DialogContentText sx={{ fontWeight: "bold" }}>
              • All Tabs: ({selectedCountAll} selected)
            </DialogContentText>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => executeAction("current")} variant="contained">
            Current Tab ({activeTabName})
          </Button>
          <Button
            onClick={() => executeAction("all")}
            variant="contained"
            color="secondary"
          >
            All Tabs
          </Button>
        </DialogActions>
      </Dialog>

      <MaterialReactTable
        {...mrtSurfaceProps}
        columns={memoizedColumns}
        data={currentTabData}
        enableTopToolbar
        enableStickyHeader
        enableSorting={false}
        autoResetPageIndex={false}
        manualPagination={false}
        onPaginationChange={setPagination}
        state={{ pagination }}
        muiTableContainerProps={{ sx: { maxHeight: 600 } }}
        muiTablePaperProps={{ sx: { boxShadow: "none" } }}
        muiTableBodyCellProps={{
          sx: {
            borderRight: "1px solid",
            borderColor: "divider",
            fontSize: typographyScale.fontSize.xs,
            padding: "0px 3px !important",
            verticalAlign: "center",
            overflow: "visible",
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: "background.default",
            color: "text.primary",
            fontWeight: typographyScale.fontWeight.semibold,
            fontSize: typographyScale.fontSize.xs,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            borderRight: "1px solid",
            borderColor: "divider",
            width: "36px",
            lineHeight: "10px",
          },
        }}
      />
    </Box>
  );
}
